
import { Component, Input, EventEmitter, Output, TemplateRef, ViewChild, OnInit, HostListener } from '@angular/core';
import { UserService } from '../../../services/user.service';
import { Business } from '../../../models/register-business';
import { ToastrService } from 'ngx-toastr';
import { ImagePreview } from '../../../models/image-preview-model';
import { KeyTitleModel } from 'src/app/models/key-value-model';
import { forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import heic2any from 'heic2any';

@Component({
  selector: 'app-product-upload',
  templateUrl: './product-upload.component.html',
  styleUrls: ['./product-upload.component.scss'],
})
export class ProductUploadComponent implements OnInit {
  @ViewChild('productUploadModal') productUploadTemplate!: TemplateRef<ProductUploadComponent>;
  @Input() businessDetails: Business;
  selectedFile: File | null = null;
  fileName: string = '';
  images: ImagePreview[] = [];
  csvDownloadUrl: string = 'api/tenants/membership/products/DownloadProductTemplate';
  uploadUrl: string = 'api/tenants/membership/products/import';
  addCategory: string = 'api/inventory/productcategories';
  @Output() onClose = new EventEmitter<any>();
  modalHeight: number;
  csvErrorContentHeight: number;
  imageErrorContentHeight: number;
  isSuccess: boolean = true;
  uploadDataResponse: any;
  productNotFound: string = '';
  duplicateImage: string = '';
  duplicateNumbers: string = '';
  duplicateNames: string = '';
  unidentifiedCategories: string = '';
  unidentifiedProducts: string = '';
  headers: string[] = [
    'itemNumber',
    'name',
    'hungeryCategory',
    'price',
    'description',
    'error'
  ];
  types: KeyTitleModel[] = [];
  isConfirmCategory: boolean = false;
  isInvalidCategory: boolean = false;
  categoryMapping: any[] = [];

  constructor(
    private userService: UserService, private toastr: ToastrService) { }

  ngOnInit() {
    this.adjustModalHeight();
    this.getProductCategories();
  }

  getProductCategories() {
    this.userService.getAll(`api/tenants/membershipApp/GetAllMembershipProductCategories?businessPartnerId=${this.businessDetails.businessPartnerId}`).subscribe((data: any) => {
      this.types = this.sortItems(data.map((item: { id: any; title: any; }) => ({
        id: item.id,
        title: item.title,
      })));
    })
  }

  downloadCSVExample = () => {
    this.userService.downloadCSV(this.csvDownloadUrl)
      .subscribe({
        next: (blob: Blob) => {
          const fileURL = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = fileURL;
          anchor.download = 'HungeryProductTemplate.csv';
          anchor.click();
          window.URL.revokeObjectURL(fileURL);
          anchor.remove();
        }
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.adjustModalHeight();

  }

  adjustModalHeight() {
    this.modalHeight = window.innerHeight * 0.65;
    this.csvErrorContentHeight = this.modalHeight * 0.32;
    this.imageErrorContentHeight = this.modalHeight * 0.32;
    if (this.uploadDataResponse != null) {
      if (this.uploadDataResponse.csvUploadProgress == 2 && this.uploadDataResponse.imageUploadProgress != 2) {
        this.csvErrorContentHeight = this.modalHeight * 0.6;
      } else if (this.uploadDataResponse.csvUploadProgress != 2 && this.uploadDataResponse.imageUploadProgress == 2) {
        this.imageErrorContentHeight = this.modalHeight * 0.6;
      }
    }
  }

  onFileSelected = (event: any) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      this.selectedFile = file;
      this.fileName = this.selectedFile.name;
    } else {
      this.toastr.error('Invalid file format. Please upload a CSV file.');
      this.selectedFile = null;
    }
  }

  async onImagesSelected(event: any) {
    const files = event.target.files;

    if (!files.length) {
      return;
    }

    for (let file of files) {
      const fileName = file.name.toLocaleLowerCase();
      const extension = fileName.split('.').pop();

      const isHeicOrHeif = file.type === 'image/heic' || file.type === 'image/heif' || (!file.type && (extension === 'heic' || extension === 'heif'));
      if (isHeicOrHeif) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg' });
          file = new File(
            [convertedBlob as Blob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
            type: 'image/jpeg'
          });
        }
        catch (error) {
          this.toastr.error('Failed to convert HEIC/HEIF image.');
          return;
        }
      }

      if (file.type.startsWith('image/')) {
        if (file.type == 'image/jpg' || file.type == 'image/png' || file.type == 'image/webp' || file.type == 'image/jpeg') {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            this.images.push({ file, preview: e.target.result });
          }
          reader.readAsDataURL(file);
          this.images.push(file);
          this.images = this.images.filter(p => p.preview);
        }
        else {
          this.toastr.error('Invalid image format. Please upload JPG, PNG or WebP files.');
        }
      } else {
        this.toastr.error('One or more selected files are not valid images');
      }
    }
  }

  removeImage = (index: number, event: Event) => {
    event.preventDefault();
    this.images.splice(index, 1);
  }

  uploadData = () => {
    if (!this.selectedFile && !this.images.length) {
      this.toastr.error('No CSV file or images selected!');
      return;
    }

    const formData = new FormData();
    if (this.selectedFile) {
      formData.append('files', this.selectedFile, this.selectedFile.name);
    }

    if (this.images.length > 0) {
      this.images.forEach(image => formData.append('files', image.file, image.file.name));
    }
    this.userService.create(`${this.uploadUrl}/${this.businessDetails.businessPartnerId}`, formData)
      .subscribe({
        next: (response) => {
          if (response != null) {
            this.uploadDataResponse = response;
            if (response.csvUploadProgress == 1 && response.imageUploadProgress == 1) {
              this.toastr.success('CSV and Images are uploaded successfully.');
              this.onCloseModal();
            }
            else if (response.csvUploadProgress == 1 && response.imageUploadProgress == 0) {
              this.toastr.success('CSV uploaded successfully.');
              this.onCloseModal();
            }
            else if (response.csvUploadProgress == 0 && response.imageUploadProgress == 1) {
              this.toastr.success('Images uploaded successfully.');
              this.onCloseModal();
            }
            else if (response.csvUploadProgress == 2 || response.imageUploadProgress == 2) {
              this.isSuccess = false;
              this.isInvalidCategory = false;
              if (this.uploadDataResponse.records.invalidCategoriesError != null && (this.uploadDataResponse.records.duplicateProductError == null &&
                this.uploadDataResponse.records.productItemNumberMissingError == null && this.uploadDataResponse.records.productPriceMissingError == null &&
                this.uploadDataResponse.records.priceConversionError == null))
                this.isInvalidCategory = true;

              if (this.uploadDataResponse.records.invalidCategoriesError != null) {
                this.categoryMapping = this.uploadDataResponse.records.invalidCategoriesError ? this.uploadDataResponse.records.invalidCategoriesError
                  .split(',').map(item => item.trim()).filter(item => item.length > 0).map(cat => ({ yourCategory: cat, mappedCategory: this.types[0] })) : [];
                this.adjustModalHeight();
              }
            }
            else {
              this.toastr.error('An error occurred during processing. Please check your file and try again.');
              this.onCloseModal();
            }
          }
        }
      });
  }

  failedCSVList = () => {
    if (this.uploadDataResponse.records.csvDetails) {
      let csvContent = this.headers
        .map(header => `"${header.replace(/"/g, '""')}"`)
        .join(',') + '\n';

      this.uploadDataResponse.records.csvDetails.forEach(item => {
        const row = this.headers.map(header => {
          const value = item[header] != null ? String(item[header]) : '';
          return `"${value.replace(/"/g, '""')}"`;
        }).join(',');
        csvContent += row + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'failedRecords.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  confirmMapping = () => {
    const requests = [];
    for (let category of this.categoryMapping) {
      const categoryData = {
        title: category.yourCategory,
        visibleCategoryId: category.mappedCategory.id,
        isForMembership: true,
        businessPartnerId: this.businessDetails.businessPartnerId,
        isMainCategory: false
      };
      const request = this.userService.create(`${this.addCategory}`, categoryData).pipe(
        tap(() => {
          this.toastr.success(`Category ${category.yourCategory} added successfully`);
        }),
        catchError(() => {
          this.toastr.error(`Category ${category.yourCategory} does not add successfully.`);
          return of(null);
        })
      );
      requests.push(request);
    }

    forkJoin(requests).subscribe(() => {
      this.uploadData();
    });
  }

  onCloseModal(callAPI: boolean = true) {
    this.selectedFile = null;
    this.fileName = null;
    this.images = [];
    this.isSuccess = true;
    this.isInvalidCategory = false;
    this.isConfirmCategory = false;
    this.onClose.emit(callAPI);
  }

  reUpload() {
    this.isSuccess = true;
  }

  sortItems(items: any[]): any[] {
    return items.slice().sort((item1, item2) => {
      if (item1.title.toLowerCase() < item2.title.toLowerCase()) {
        return -1;
      }
      if (item1.title.toLowerCase() > item2.title.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }
}
